import { SegmentedControl, Switch } from '@mantine/core';
import { BaremetalInstance, BaremetalPaymentPeriod } from '@xver/@types/instance';
import { ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useChangeBaremetalPaymentPeriodMutate } from '@xver/hooks/CloudComputeHooks/useChangeBaremetalPaymentPeriodMutate';
import { useToggleBaremetalAutoRenewMutate } from '@xver/hooks/CloudComputeHooks/useToggleBaremetalAutoRenewMutate';
import { priceForPeriod } from '@xver/utils/baremetal';
import { formatDate } from '@xver/utils/date';
import { format } from '@xver/utils/number';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoRow from './InfoRow';

const PERIODS: BaremetalPaymentPeriod[] = ['weekly', 'monthly', 'yearly'];

interface BillingCardProps {
    baremetal: BaremetalInstance;
    /** False with view-only access: auto-renew and the payment period are shown read-only. */
    canManage: boolean;
}

export default function BillingCard({ baremetal, canManage }: BillingCardProps) {
    const { t, i18n } = useTranslation('bareMetal');
    const periodLabelId = useId();
    const autoRenewMutation = useToggleBaremetalAutoRenewMutate();
    const periodMutation = useChangeBaremetalPaymentPeriodMutate();
    const [targetPeriod, setTargetPeriod] = useState<BaremetalPaymentPeriod | null>(null);

    const periodLabel = (p: BaremetalPaymentPeriod) => t(`bareMetalDeployment:paymentPeriod.${p}`);
    const hasNextCharge = new Date(baremetal.next_charge).getFullYear() > 1;

    const confirmPeriod = () => {
        if (!targetPeriod) return;
        periodMutation.mutate(
            { id: baremetal.id, period: targetPeriod },
            { onSettled: () => setTargetPeriod(null) }
        );
    };

    return (
        <DialogBox label={t('detail.billing.title')} classNames="p-4 gap-6">
            <dl className="w-full grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-4 m-0">
                <InfoRow
                    label={t('detail.billing.plan')}
                    value={periodLabel(baremetal.payment_period)}
                />
                <InfoRow
                    label={t('detail.billing.price')}
                    value={`฿${format(priceForPeriod(baremetal))} / ${periodLabel(baremetal.payment_period)}`}
                />
                <InfoRow
                    label={t('detail.billing.nextCharge')}
                    value={hasNextCharge && formatDate(baremetal.next_charge, i18n.language, t)}
                />
            </dl>

            <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="flex flex-col">
                    <span className="text-base text-fg">{t('detail.billing.autoRenew')}</span>
                    <span className="text-sm text-fg/68">
                        {t('detail.billing.autoRenewDescription')}
                    </span>
                </div>
                <Switch
                    aria-label={t('detail.billing.autoRenew')}
                    checked={baremetal.auto_renew}
                    onChange={() => autoRenewMutation.mutate(baremetal.id)}
                    disabled={autoRenewMutation.isPending || !canManage}
                    size="md"
                    color="green"
                />
            </div>

            <div className="w-full flex flex-col gap-2">
                <div className="flex flex-col">
                    <span id={periodLabelId} className="text-base text-fg">
                        {t('detail.billing.paymentPeriod')}
                    </span>
                    <span className="text-sm text-fg/68">
                        {t('detail.billing.paymentPeriodDescription')}
                    </span>
                </div>
                <SegmentedControl
                    aria-labelledby={periodLabelId}
                    fullWidth
                    value={baremetal.payment_period}
                    onChange={(value) => {
                        const next = value as BaremetalPaymentPeriod;
                        if (next !== baremetal.payment_period) setTargetPeriod(next);
                    }}
                    disabled={periodMutation.isPending || !canManage}
                    data={PERIODS.map((p) => ({ value: p, label: periodLabel(p) }))}
                />
            </div>

            <ConfirmActionModal
                opened={targetPeriod !== null}
                onClose={() => setTargetPeriod(null)}
                title={t('detail.billing.changePeriodTitle')}
                message={t('detail.billing.changePeriodMessage', {
                    from: periodLabel(baremetal.payment_period),
                    to: targetPeriod ? periodLabel(targetPeriod) : '',
                })}
                confirmLabel={t('detail.billing.changePeriodConfirm')}
                isLoading={periodMutation.isPending}
                onConfirm={confirmPeriod}
            />
        </DialogBox>
    );
}
